import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowUp, MessageSquareText, Sparkles, X } from 'lucide-react';
import { displayName, parseMailbox, type AskResult, type AskTurn } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { listTime } from '@/lib/format';
import { Avatar } from './Avatar';
import { cn, IconButton } from './ui';

interface Turn {
  q: string;
  result?: AskResult;
  error?: string;
}

const STARTERS = [
  'What needs my attention today?',
  'Who have I not replied to this week?',
  'Summarize what happened this week',
  'Any invoices or payments due soon?',
];

/** Chat with your mailbox: answers come from the AI, citing the emails they're based on. */
export function AskPanel() {
  const open = useUi((s) => s.askOpen);
  const seed = useUi((s) => s.askSeed);
  const close = useUi((s) => s.closeAsk);
  const setView = useUi((s) => s.setView);
  const select = useUi((s) => s.select);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  const ask = async (q: string) => {
    if (!q.trim() || busy) return;
    setText('');
    setBusy(true);
    const history: AskTurn[] = turns.flatMap((t) => (t.result ? [{ role: 'user' as const, text: t.q }, { role: 'assistant' as const, text: t.result.answer }] : []));
    setTurns((t) => [...t, { q }]);
    try {
      const result = await api.ask(q, history);
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, result } : x)));
    } catch (e) {
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, error: errorMessage(e) } : x)));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open && seed) void ask(seed);
    if (open) setTimeout(() => input.current?.focus(), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed]);

  useEffect(() => bottom.current?.scrollIntoView({ behavior: 'smooth' }), [turns, busy]);

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ x: 460, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 460, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 38 }}
          className="glass fixed bottom-3 right-3 top-[56px] z-40 flex w-[440px] flex-col overflow-hidden rounded-3xl bg-raised/90 shadow-[0_30px_100px_-20px_rgba(0,0,0,.6)]"
        >
          <div className="flex items-center gap-2.5 border-b border-line px-5 py-3.5">
            <span className="flex size-7 items-center justify-center rounded-lg ai-gradient text-white"><MessageSquareText className="size-4" /></span>
            <div className="flex-1">
              <div className="text-[14px] font-semibold">Ask your inbox</div>
              <div className="text-[11.5px] text-muted">Answers cite the emails they come from</div>
            </div>
            {turns.length > 0 && <button onClick={() => setTurns([])} className="text-[12px] text-muted hover:text-fg">Clear</button>}
            <IconButton label="Close" size="sm" onClick={close}><X /></IconButton>
          </div>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
            {turns.length === 0 && (
              <div className="pt-6">
                <div className="mb-1 font-display text-[30px] leading-tight">What would you like to know?</div>
                <div className="mb-5 text-[13px] text-muted">AI reads your recent mail to answer.</div>
                <div className="space-y-2">
                  {STARTERS.map((s) => (
                    <button key={s} onClick={() => void ask(s)} className="flex w-full items-center gap-2.5 rounded-xl border border-line px-3.5 py-2.5 text-left text-[13px] transition hover:border-accent/40 hover:bg-accent/5">
                      <Sparkles className="size-3.5 text-accent" />
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {turns.map((t, i) => (
              <div key={i} className="space-y-3">
                <div className="ml-10 rounded-2xl rounded-br-md bg-accent px-3.5 py-2 text-[13.5px] text-accent-fg">{t.q}</div>
                {t.error ? (
                  <div className="text-[13px] text-danger">{t.error}</div>
                ) : !t.result ? (
                  <div className="flex items-center gap-2 text-[13px]"><Sparkles className="size-4 animate-pulse text-accent" /><span className="shimmer-text">Reading your mail…</span></div>
                ) : (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-2.5">
                    <div className="selectable whitespace-pre-wrap text-[14px] leading-relaxed">{t.result.answer}</div>
                    {t.result.messages.length > 0 && (
                      <div className="space-y-1.5">
                        {t.result.messages.map((m) => {
                          const from = parseMailbox(m.from) ?? { address: m.fromAddress };
                          return (
                            <button
                              key={m.id}
                              onClick={() => {
                                setView({ kind: 'mail', folder: m.folder });
                                select(m.id);
                              }}
                              className="flex w-full items-center gap-2.5 rounded-xl border border-line bg-bg/40 px-2.5 py-2 text-left transition hover:border-accent/40"
                            >
                              <Avatar name={displayName(from)} address={from.address} size={26} />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-[12.5px] font-medium">{m.subject}</span>
                                <span className="block truncate text-[11.5px] text-muted">{displayName(from)} · {listTime(m.receivedUtc)}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {i === turns.length - 1 && t.result.followUps.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {t.result.followUps.map((f) => (
                          <button key={f} onClick={() => void ask(f)} className="rounded-full border border-line px-2.5 py-1 text-[12px] text-muted transition hover:border-accent/40 hover:text-fg">{f}</button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ))}
            <div ref={bottom} />
          </div>

          <div className="p-3">
            <div className={cn('ai-ring flex items-end gap-2 rounded-2xl bg-bg/50 p-2 pl-3.5', busy && 'busy')}>
              <textarea
                ref={input}
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void ask(text);
                  }
                }}
                placeholder="Ask about your email…"
                className="max-h-32 min-h-[36px] flex-1 resize-none bg-transparent py-2 text-[13.5px] outline-none placeholder:text-faint"
              />
              <button onClick={() => void ask(text)} disabled={!text.trim() || busy} className="flex size-9 items-center justify-center rounded-xl ai-gradient text-white transition disabled:opacity-40">
                <ArrowUp className="size-4" />
              </button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
